import './style.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import NiceModal from '@ebay/nice-modal-react';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@material-ui/lab';
import { IoMdClose } from 'react-icons/io';
import { MdArrowBack, MdKeyboardArrowUp } from 'react-icons/md';

import { Preloader, Text } from 'pctComponents/atoms';
import { updateConfigHeaderFooter } from 'actions';
import { getTeamMeetingMonths, getTeamMeetingsByMonth } from 'services/api';
import CardMeeting from 'pctComponents/organisms/Meeting/components/CardMeeting';
import ModalMeetingDetails from 'pctComponents/modals/ModalMeetingDetails';
import {
    MeetingItem,
    MeetingMonth,
    fillMonthGaps,
    formatMonthName,
    formatMonthTitle,
    getDefaultOpenMonth,
    monthKey,
    sortByStartDate,
} from 'pctComponents/organisms/Meeting/utils';

export type MonthsFetcher = (idTeam: number, nodeCode: string) => Promise<MeetingMonth[]>;
export type ByMonthFetcher = (idTeam: number, nodeCode: string, year: number, month: number) => Promise<MeetingItem[]>;

export const buildAllMeetingsPath = (idTeam: number, nodeCode: string): string =>
    `/meetings/${idTeam}/${encodeURIComponent(nodeCode)}`;

type Props = {
    idTeam?: number;
    nodeCode?: string;
    fallbackTitle?: string;
    fetchMonths?: MonthsFetcher;
    fetchByMonth?: ByMonthFetcher;
};

const unwrap = <T,>(response: any): T[] => {
    const data = response?.data ?? response;

    if (Array.isArray(data)) {
        return data;
    }

    return data?.result || [];
};

const defaultFetchMonths: MonthsFetcher = async (idTeam, nodeCode) =>
    unwrap<MeetingMonth>(await getTeamMeetingMonths(idTeam, nodeCode));

const defaultFetchByMonth: ByMonthFetcher = async (idTeam, nodeCode, year, month) =>
    unwrap<MeetingItem>(await getTeamMeetingsByMonth(idTeam, nodeCode, year, month));

const MonthSkeleton = ({ count = 3 }: { count?: number }) => (
    <div className="monthGrid monthSkeleton">
        {Array.from({ length: Math.min(count, 3) }).map((_, index) => (
            <Skeleton key={index} variant="rect" className="monthSkeletonCard" />
        ))}
    </div>
);

type MonthSectionProps = {
    month: MeetingMonth;
    isOpen: boolean;
    onToggle: () => void;
    idTeam: number;
    nodeCode: string;
    fallbackTitle: string;
    fetchByMonth: ByMonthFetcher;
    onSelectMeeting: (meeting: MeetingItem) => void;
};

const MonthSection = ({
    month,
    isOpen,
    onToggle,
    idTeam,
    nodeCode,
    fallbackTitle,
    fetchByMonth,
    onSelectMeeting,
}: MonthSectionProps) => {
    const isEmpty = month.count === 0;

    const { data, isLoading, isError } = useQuery({
        queryKey: ['teamMeetingsByMonth', idTeam, nodeCode, month.year, month.month],
        queryFn: () => fetchByMonth(idTeam, nodeCode, month.year, month.month),
        enabled: isOpen && !isEmpty,
    });

    const meetings = useMemo(() => sortByStartDate(data || []), [data]);

    return (
        <section className={`monthSection ${isOpen ? 'monthSectionOpen' : ''}`}>
            <button type="button" className="monthHeader" onClick={onToggle} aria-expanded={isOpen}>
                <span className="monthTitle">{formatMonthTitle(month)}</span>
                <span className="monthCount">
                    {isEmpty ? '(Nenhum evento)' : `(${month.count} ${month.count === 1 ? 'evento' : 'eventos'})`}
                </span>
                <MdKeyboardArrowUp className="monthChevron" />
            </button>

            {isOpen && (
                <div className="monthBody">
                    {isEmpty && (
                        <div className="monthEmpty">
                            <Text component="p" text={`Nenhum evento em ${formatMonthName(month)}.`} className="monthEmptyTitle" />
                            <Text component="p" text="Não há eventos programados para este mês." />
                        </div>
                    )}

                    {!isEmpty && isLoading && <MonthSkeleton count={month.count} />}

                    {!isEmpty && isError && (
                        <Text component="p" text="Não foi possível carregar os eventos deste mês." className="monthError" />
                    )}

                    {!isEmpty && !isLoading && !isError && (
                        <div className="monthGrid">
                            {meetings.map((meeting) => (
                                <CardMeeting
                                    key={meeting.idMeeting}
                                    meeting={meeting}
                                    highlight
                                    fallbackTitle={fallbackTitle}
                                    onSelect={onSelectMeeting}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

const AllMeetings = ({
    idTeam: idTeamProp,
    nodeCode: nodeCodeProp,
    fallbackTitle = '',
    fetchMonths = defaultFetchMonths,
    fetchByMonth = defaultFetchByMonth,
}: Props) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const params = useParams<{ idTeam?: string; nodeCode?: string }>();

    useEffect(() => {
        dispatch(updateConfigHeaderFooter({ prevNavStyle: 'hide', backCloseText: '' }));
    }, [dispatch]);

    const idTeam = idTeamProp ?? Number(params.idTeam);
    const nodeCode = nodeCodeProp ?? decodeURIComponent(params.nodeCode || '');

    const [openMonth, setOpenMonth] = useState<string | null | undefined>(undefined);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['teamMeetingMonths', idTeam, nodeCode],
        queryFn: () => fetchMonths(idTeam, nodeCode),
        enabled: Boolean(idTeam && nodeCode),
    });

    const months = useMemo(() => fillMonthGaps(data || []), [data]);
    const total = useMemo(() => months.reduce((sum, item) => sum + item.count, 0), [months]);

    useEffect(() => {
        if (months.length && openMonth === undefined) {
            setOpenMonth(getDefaultOpenMonth(months));
        }
    }, [months, openMonth]);

    const goBack = () => {
        if (history.length > 1) {
            history.goBack();
            return;
        }

        history.push('/');
    };

    const toggleMonth = (key: string) => setOpenMonth((current) => (current === key ? null : key));

    const openDetails = (meeting: MeetingItem) => {
        NiceModal.show(ModalMeetingDetails, { obj: { meeting, fallbackTitle } });
    };

    return (
        <div className="pctPage pctPageBlock allMeetingsPage">
            <div className="allMeetingsHeader">
                <div className="allMeetingsHeading">
                    <button type="button" className="backBtn" onClick={goBack} aria-label="">
                        <MdArrowBack />
                        <Text component="h2" text="Ver todos os eventos" className="title" />
                    </button>
                    {!isLoading && !isError && (
                        <Text
                            component="p"
                            text={`${total} ${total === 1 ? 'evento' : 'eventos'}`}
                            className="subtitle"
                        />
                    )}
                </div>

                {/* <button type="button" className="closeBtn" onClick={goBack} aria-label="Fechar">
                    <IoMdClose />
                </button> */}
            </div>

            <div className="allMeetingsContent">
                {isLoading && <Preloader />}

                {isError && <Text component="p" text="Não foi possível carregar os eventos." className="monthError" />}

                {!isLoading && !isError && !months.length && (
                    <Text component="p" text="Nenhum evento programado." className="monthError" />
                )}

                {months.map((month) => {
                    const key = monthKey(month);

                    return (
                        <MonthSection
                            key={key}
                            month={month}
                            isOpen={openMonth === key}
                            onToggle={() => toggleMonth(key)}
                            idTeam={idTeam}
                            nodeCode={nodeCode}
                            fallbackTitle={fallbackTitle}
                            fetchByMonth={fetchByMonth}
                            onSelectMeeting={openDetails}
                        />
                    );
                })}

                {!isLoading && !isError && months.length > 0 && (
                    <Text
                        component="p"
                        text="Você chegou ao fim. Não há mais eventos programados."
                        className="endOfList"
                    />
                )}
            </div>
        </div>
    );
};

export default AllMeetings;
